"use client";
import React, {
  createContext,
  useContext,
  useCallback,
  useState,
} from "react";
import { BASE_URL } from "../config/config";

const CourseCategoryContext = createContext();

export const CourseCategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH COURSE CATEGORIES
  const fetchCourseCategories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/course-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();

      if (res.ok && data.status === 200) {
        setCategories(data.data || []);
      } else {
        setError(data.message || "Failed to fetch categories");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD COURSE CATEGORY
  const addCourseCategory = async ({ name, status = "Active" }) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        name,
        status,
        create_audit_id: user.id,
      };

      const res = await fetch(`${BASE_URL}/course-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && (data.status === 200 || data.status === 201)) {
        setCategories((prev) => [data.data, ...prev]);
        return true;
      } else {
        alert(data.message || "Add failed");
        return false;
      }
    } catch (err) {
      console.error(err);
      alert("Network error");
      return false;
    }
  };

  // 🔹 UPDATE COURSE CATEGORY
  const updateCourseCategory = async (id, payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/course-categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setCategories((prev) =>
          prev.map((c) => (c.id === id ? { ...c, ...payload } : c))
        );
        return true;
      } else {
        alert(data.message || "Update failed");
      }
    } catch {
      alert("Network error");
    }
  };

  // 🔹 DELETE COURSE CATEGORY
  const deleteCourseCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/course-categories/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setCategories((prev) => prev.filter((c) => c.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch {
      alert("Delete failed");
    }
  };

  // 🔹 TOGGLE STATUS
  const toggleCourseCategoryStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(
        `${BASE_URL}/course-categories/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            update_audit_id: user.id,
          }),
        }
      );

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setCategories((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, status: newStatus } : c
          )
        );
      } else {
        alert(data.message || "Status update failed");
      }
    } catch {
      alert("Status update failed");
    }
  };

  return (
    <CourseCategoryContext.Provider
      value={{
        categories,
        loading,
        error,
        fetchCourseCategories,
        addCourseCategory,
        updateCourseCategory,
        deleteCourseCategory,
        toggleCourseCategoryStatus,
      }}
    >
      {children}
    </CourseCategoryContext.Provider>
  );
};

export const useCourseCategories = () =>
  useContext(CourseCategoryContext);
